import { Position } from '../types';
import type { Rect, Viewport } from '../types';

/** Gap (screen px) between a node and its toolbar (v11 default). */
export const DEFAULT_TOOLBAR_OFFSET = 10;

/** Where along the node's side the toolbar sits. */
export type ToolbarAlign = 'start' | 'center' | 'end';

const ALIGN_FACTOR: Record<ToolbarAlign, number> = { start: 0, center: 0.5, end: 1 };
const PERCENT = 100;

/**
 * CSS transform that places a toolbar beside a node, in renderer (screen) coordinates: it moves with pan and zoom
 * but keeps its own size. The second translate shifts the toolbar by its own size so it never covers the node.
 */
export const getNodeToolbarTransform = (
  nodeRect: Rect,
  { x: tx, y: ty, zoom }: Viewport,
  position: Position,
  offset: number,
  align: ToolbarAlign,
): string => {
  const left = nodeRect.x * zoom + tx;
  const top = nodeRect.y * zoom + ty;
  const width = nodeRect.width * zoom;
  const height = nodeRect.height * zoom;
  const along = ALIGN_FACTOR[align];
  const shiftAlong = -along * PERCENT;
  const placements: Record<Position, [number, number, number, number]> = {
    [Position.Top]: [left + width * along, top - offset, shiftAlong, -PERCENT],
    [Position.Bottom]: [left + width * along, top + height + offset, shiftAlong, 0],
    [Position.Left]: [left - offset, top + height * along, -PERCENT, shiftAlong],
    [Position.Right]: [left + width + offset, top + height * along, 0, shiftAlong],
  };
  const [x, y, shiftX, shiftY] = placements[position];
  return `translate(${x}px, ${y}px) translate(${shiftX}%, ${shiftY}%)`;
};
